import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Spin,
  Form,
  Card,
  Input,
  DatePicker,
  Dropdown,
  Divider,
  Tooltip,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { connect } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";

import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../components/layouts/enhancement/TopContent";

import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import Settings01 from "@untitled-ui/icons-react/build/esm/Settings01";
import ArrowLeft from "@untitled-ui/icons-react/build/esm/ArrowLeft";

import cnt from "../../../services/definedConstant";
import { rightAction } from "../../../redux/actions";

import {
  labelConfig,
  staticConfig,
  apiConfig,
} from "../treasuryContractBooking.config";

import { BOM } from "../../../services/definedMethod";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const parseDate = (v) => {
  if (!v) return null;

  if (Array.isArray(v)) {
    const [y, m, d] = v;

    return dayjs(
      `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
    );
  }

  return dayjs(v);
};

const formatAmount = (v, currency = "") => {
  if (v == null || v === "") return "—";

  const formatted = `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  return currency ? `${currency} ${formatted}` : formatted;
};

const formatBondType = (value) => {
  if (!value) return "—";

  return value.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

const ViewBondContract = (props) => {
  const [form] = Form.useForm();

  const navigate = useNavigate();
  const location = useLocation();

  const { userData } = props;
  const { session } = userData || {};

  const contractId = location.state || null;

  const [isLoading, setIsLoading] = useState(false);
  const [contract, setContract] = useState(null);

  useEffect(() => {
    if (contractId) {
      fetchContractDetail(contractId);
    }
  }, [contractId]);

  const fetchContractDetail = (id) => {
    setIsLoading(true);

    const body = { session };
    const url = `${apiConfig.GetBondContracts}/${id}`;

    BOM.FetchReqAction(body, url, (err, res) => {
      setIsLoading(false);

      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.defaultUserMessage || "Failed to load bond contract.",
        );

        return;
      }

      setContract(res);

      const currency = res.currency?.code ?? "";

      form.setFieldsValue({
        client: res.client?.displayName,
        contractReferenceId: res.contractReferenceId,

        product: res.product
          ? `${res.product.productName} (${res.product.productCode})`
          : "—",

        settlementAccount: res.settlementAccount?.accountNo,

        custodian: res.custodian?.name,

        investorType: res.investorType?.text,

        bondType: formatBondType(res.bondType),

        issuer: res.issuer,

        fixedCouponRate:
          res.fixedCouponRate != null ? `${res.fixedCouponRate}%` : "—",

        paymentType: res.paymentType?.text,

        dealInitiationDate: parseDate(res.dealInitiationDate),

        issueDate: parseDate(res.issueDate),

        settlementDate: parseDate(res.settlementDate),

        tenor: res.tenor != null ? `${res.tenor} days` : "—",

        amount: formatAmount(res.amount, currency),

        purchasePrice: formatAmount(res.purchasePrice, currency),

        numberOfUnits: res.numberOfUnits != null ? `${res.numberOfUnits}` : "—",

        totalConsideration: formatAmount(res.totalConsideration, currency),

        netMaturityValue: formatAmount(res.netMaturityValue, currency),

        currency: res.currency
          ? `${res.currency.code} (${res.currency.decimalPlaces} d.p.)`
          : "—",

        totalInterestAccrued: formatAmount(res.totalInterestAccrued, currency),

        totalInterestPosted: formatAmount(res.totalInterestPosted, currency),

        totalFees: formatAmount(res.totalFees, currency),

        totalPaidOut: formatAmount(res.totalPaidOut, currency),

        kycStatus: res.kycStatus?.text,
        amlScreening: res.amlScreening?.text,
        sanctionScreening: res.sanctionScreening?.text,
        centralBankReport: res.centralBankReport?.text,
        issuerRiskRating: res.issuerRiskRating?.text,
        marketRiskConsideration: res.marketRiskConsideration?.text,
        concentrationRisk: res.concentrationRisk?.text,

        applicationFormStatus: res.applicationFormStatus?.text,

        bondPurchaseAgreement: res.bondPurchaseAgreement?.text,

        identificationDocuments: res.identificationDocuments?.text,

        corporateResolution: res.corporateResolution?.text,

        taxClearanceCertificate: res.taxClearanceCertificate?.text,
      });
    });
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Actions
  // ───────────────────────────────────────────────────────────────────────────

  const runAction = (key) => {
    setIsLoading(true);

    const body = { session };

    const actionMap = {
      approve: {
        url: `${apiConfig.GetBondContracts}/${contractId}/approve`,
        successMsg: "Contract approved successfully.",
      },

      reject: {
        url: `${apiConfig.GetBondContracts}/${contractId}/reject`,
        successMsg: "Contract rejected.",
        goBack: true,
      },

      disburse: {
        url: `${apiConfig.GetBondContracts}/${contractId}/disburse`,
        successMsg: "Contract disbursed successfully.",
      },

      redeem_callable: {
        url: `${apiConfig.GetBondContracts}/${contractId}/redeem?type=callable`,
        successMsg: "Callable redemption processed.",
      },

      redeem_puttable: {
        url: `${apiConfig.GetBondContracts}/${contractId}/redeem?type=puttable`,
        successMsg: "Puttable redemption processed.",
      },
    };

    const action = actionMap[key];

    if (!action) {
      setIsLoading(false);
      return;
    }

    BOM.FetchReqAction(body, action.url, (err) => {
      setIsLoading(false);

      if (err) {
        BOM.AlertMsg(cnt.DANGER, err?.defaultUserMessage || "Action failed.");

        return;
      }

      BOM.AlertMsg(cnt.SUCCESS, action.successMsg);

      if (action.goBack) {
        navigate(-1);
      } else {
        fetchContractDetail(contractId);
      }
    });
  };

  const handleAction = ({ key }) => {
    switch (key) {
      case "approve":
        navigate("/bond-contract/approve", { state: contractId });
        break;
      case "reject":
        navigate("/bond-contract/reject", { state: contractId });
        break;
      case "disburse":
        navigate("/bond-contract/disburse", { state: contractId });
        break;
      case "redeem_callable":
        navigate("/bond-contract/premature-redemption", { state: contractId });
        break;
      case "redeem_puttable":
        navigate("/bond-contract/puttable-redemption", { state: contractId });
        break;
      case "get_transactions":
        navigate("/bond-contract/transactions", { state: contractId });
        break;
      default:
        break;
    }
  };
  const actionMenuItems = [
    { key: "approve", label: "Approve Contract" },
    { key: "reject", label: "Reject Contract" },
    { type: "divider" },
    { key: "disburse", label: "Disburse Contract" },
    { key: "redeem_callable", label: "Premature Redemption (Callable)" },
    { key: "redeem_puttable", label: "Puttable Premature Redemption" },
    { type: "divider" },
    { key: "get_transactions", label: "View Transactions" },
  ];

  const ds = {
    height: 44,
    background: "#F9FAFB",
    cursor: "not-allowed",
  };

  const Tip = ({ text, tip }) => (
    <span>
      {text}

      <Tooltip title={tip}>
        <InfoCircleOutlined
          style={{
            marginLeft: 4,
            color: "#667085",
          }}
        />
      </Tooltip>
    </span>
  );

  // ───────────────────────────────────────────────────────────────────────────
  // UI
  // ───────────────────────────────────────────────────────────────────────────

  return (
    <MainLayout pageItems={staticConfig.viewBondContract}>
      <TopContent pageTitle={labelConfig.bondContractListing} />

      <Spin spinning={isLoading} size="large">
        <div style={{ padding: "24px" }}>
          <Card
            style={{
              boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.08)",
              borderRadius: 20,
            }}
          >
            {/* Header */}
            <Row
              justify="space-between"
              align="middle"
              style={{ marginBottom: 24 }}
            >
              <Col>
                <h3
                  style={{
                    margin: 0,
                    fontWeight: 600,
                  }}
                >
                  Bond Contract Details
                </h3>
              </Col>

              <Col>
                <Dropdown
                  menu={{
                    items: actionMenuItems,
                    onClick: handleAction,
                  }}
                  trigger={["click"]}
                  placement="bottomRight"
                >
                  <button
                    type="button"
                    style={{
                      height: 44,
                      padding: "0 20px",
                      borderRadius: 8,
                      border: "1px solid #D0D5DD",
                      background: "#fff",
                      color: "#344054",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <Settings01 width={16} height={16} color="#344054" />
                    Actions
                    <ChevronDown width={16} height={16} color="#667085" />
                  </button>
                </Dropdown>
              </Col>
            </Row>

            {/* Form */}
            <Form layout="vertical" form={form}>
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Client"
                        tip="The client this bond contract belongs to."
                      />
                    }
                    name="client"
                  >
                    <Input style={ds} disabled />
                  </Form.Item>
                </Col>

                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Contract Reference"
                        tip="Unique reference ID for this bond contract."
                      />
                    }
                    name="contractReferenceId"
                  >
                    <Input style={ds} disabled />
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Product"
                        tip="The bond product linked to this contract."
                      />
                    }
                    name="product"
                  >
                    <Input style={ds} disabled />
                  </Form.Item>
                </Col>

                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Settlement Account"
                        tip="The client account used for settlement."
                      />
                    }
                    name="settlementAccount"
                  >
                    <Input style={ds} disabled />
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Custodian"
                        tip="The custodian holding the bond."
                      />
                    }
                    name="custodian"
                  >
                    <Input style={ds} disabled />
                  </Form.Item>
                </Col>

                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Investor Type"
                        tip="The classification of the investor."
                      />
                    }
                    name="investorType"
                  >
                    <Input style={ds} disabled />
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Bond Type"
                        tip="The type of bond being contracted."
                      />
                    }
                    name="bondType"
                  >
                    <Input style={ds} disabled />
                  </Form.Item>
                </Col>

                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip text="Issuer" tip="The entity issuing the bond." />
                    }
                    name="issuer"
                  >
                    <Input style={ds} disabled />
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Fixed Coupon Rate (%)"
                        tip="The fixed interest rate paid on the bond."
                      />
                    }
                    name="fixedCouponRate"
                  >
                    <Input style={ds} disabled />
                  </Form.Item>
                </Col>

                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Payment Type"
                        tip="How coupon payments are made."
                      />
                    }
                    name="paymentType"
                  >
                    <Input style={ds} disabled />
                  </Form.Item>
                </Col>
              </Row>

              {/* Dates */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Deal Initiation Date"
                        tip="The date the bond deal was initiated."
                      />
                    }
                    name="dealInitiationDate"
                  >
                    <DatePicker
                      style={{
                        width: "100%",
                        height: 44,
                        background: "#F9FAFB",
                        cursor: "not-allowed",
                      }}
                      format="YYYY-MM-DD"
                      disabled
                    />
                  </Form.Item>
                </Col>

                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Issue Date"
                        tip="The date the bond is issued."
                      />
                    }
                    name="issueDate"
                  >
                    <DatePicker
                      style={{
                        width: "100%",
                        height: 44,
                        background: "#F9FAFB",
                        cursor: "not-allowed",
                      }}
                      format="YYYY-MM-DD"
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Settlement Date"
                        tip="The expected settlement / maturity date."
                      />
                    }
                    name="settlementDate"
                  >
                    <DatePicker
                      style={{
                        width: "100%",
                        height: 44,
                        background: "#F9FAFB",
                        cursor: "not-allowed",
                      }}
                      format="YYYY-MM-DD"
                      disabled
                    />
                  </Form.Item>
                </Col>

                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Tenor (Days)"
                        tip="The number of days from issue to maturity."
                      />
                    }
                    name="tenor"
                  >
                    <Input style={ds} disabled />
                  </Form.Item>
                </Col>
              </Row>

              {/* Financial */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Amount (Face Value)"
                        tip="The face / par value of the bond."
                      />
                    }
                    name="amount"
                  >
                    <Input style={ds} disabled />
                  </Form.Item>
                </Col>

                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Purchase Price"
                        tip="The actual price paid to purchase the bond."
                      />
                    }
                    name="purchasePrice"
                  >
                    <Input style={ds} disabled />
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Number of Units"
                        tip="The number of bond units purchased."
                      />
                    }
                    name="numberOfUnits"
                  >
                    <Input style={ds} disabled />
                  </Form.Item>
                </Col>

                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Total Consideration"
                        tip="Total amount payable."
                      />
                    }
                    name="totalConsideration"
                  >
                    <Input style={ds} disabled />
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Net Maturity Value"
                        tip="The total value expected at maturity."
                      />
                    }
                    name="netMaturityValue"
                  >
                    <Input style={ds} disabled />
                  </Form.Item>
                </Col>

                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Currency"
                        tip="Currency of the bond contract."
                      />
                    }
                    name="currency"
                  >
                    <Input style={ds} disabled />
                  </Form.Item>
                </Col>
              </Row>

              {/* Totals */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Total Interest Accrued"
                        tip="Cumulative interest accrued to date."
                      />
                    }
                    name="totalInterestAccrued"
                  >
                    <Input style={ds} disabled />
                  </Form.Item>
                </Col>

                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Total Interest Posted"
                        tip="Cumulative interest posted."
                      />
                    }
                    name="totalInterestPosted"
                  >
                    <Input style={ds} disabled />
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Total Fees"
                        tip="Total fees charged on this contract."
                      />
                    }
                    name="totalFees"
                  >
                    <Input style={ds} disabled />
                  </Form.Item>
                </Col>

                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Total Paid Out"
                        tip="Total amount paid out so far."
                      />
                    }
                    name="totalPaidOut"
                  >
                    <Input style={ds} disabled />
                  </Form.Item>
                </Col>
              </Row>

              {/* Compliance */}
              <Divider orientation="left">Compliance & Risk</Divider>

              <Row>
                {[
                  ["kycStatus", "KYC Status"],
                  ["amlScreening", "AML Screening"],
                  ["sanctionScreening", "Sanction Screening"],
                  ["centralBankReport", "Central Bank Report"],
                  ["issuerRiskRating", "Issuer Risk Rating"],
                  ["marketRiskConsideration", "Market Risk Consideration"],
                  ["concentrationRisk", "Concentration Risk"],
                ].map(([name, label]) => (
                  <Col span={12} key={name} style={{ padding: "0 8px" }}>
                    <Form.Item label={label} name={name}>
                      <Input style={ds} disabled />
                    </Form.Item>
                  </Col>
                ))}
              </Row>

              {/* Documentation */}
              <Divider orientation="left">Documentation Status</Divider>

              <Row>
                {[
                  ["applicationFormStatus", "Application Form"],

                  ["bondPurchaseAgreement", "Bond Purchase Agreement"],

                  ["identificationDocuments", "Identification Documents"],

                  ["corporateResolution", "Corporate Resolution"],

                  ["taxClearanceCertificate", "Tax Clearance Certificate"],
                ].map(([name, label]) => (
                  <Col span={12} key={name} style={{ padding: "0 8px" }}>
                    <Form.Item label={label} name={name}>
                      <Input style={ds} disabled />
                    </Form.Item>
                  </Col>
                ))}
              </Row>
            </Form>

            <Divider style={{ margin: "8px 0 20px" }} />

            {/* Footer */}
            <Row justify="end">
              <Col>
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  style={{
                    height: 44,
                    padding: "0 24px",
                    borderRadius: 8,
                    border: "1px solid #D0D5DD",
                    background: "#fff",
                    color: "#344054",
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <ArrowLeft width={16} height={16} color="#344054" />
                  Back
                </button>
              </Col>
            </Row>
          </Card>
        </div>
      </Spin>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  rightBtn: state.RightSideBtn,
  userData: state.AuthenticationData.userData,
});

export default connect(mapStateToProps, {
  rightAction,
})(ViewBondContract);
